<template>
    <div
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <show-all-new
            :title="pageTitle"
            :items="items"
            :is-loading="isLoading"
            @item-selected="itemsSelected"
            @request-more="fetchMoreItems"
        />
    </div>
</template>

<script lang="ts" setup >
    import { ref, type Ref, type ComputedRef, computed } from "vue";
    import type { ProgramCardData, OnDemandCardData } from "@/components/Cards/types";
    import { linkItems, ShowAllContentType } from "@/components/Header/types/LinkItemsCollection";
    import { PlayItem, type ProgramGuideItem } from "@ca.vmedia.tv/domains";
    import { useApplicationStore } from "@/stores/Application";
    import { useRoute, useRouter } from "vue-router";
    import { storeToRefs } from 'pinia'
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useHomePageStore } from "@/stores/HomePage";
    import { usePlayerStore } from "@/stores/Player";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { Strings } from "@/locales/types/enums/strings";

    const DetailPageStoreModule = useDetailPageStore();
    const {
        setViewedDetailItems,
        setCurrentItemDetail
    } = DetailPageStoreModule;

    const ApplicationStore = useApplicationStore();
    const {
        showPreviewSignInModal, 
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;
    const {
        startPlayingProgram,
    } = usePlayerStore();

    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const HomePageStore = useHomePageStore();
    const {
        loadShowAllNextPage,
        fetchEpisodes
    } = HomePageStore;

    const {
        getShowAll: items,
        getIsLoadingShowAll: isLoading,
        getContinueWatchingTitle,
        getMoviesTitle,
        getSportsTitle,
        getKidsTitle,
        getNewsTitle,
        getOlympicsTitle,
        getWatchAgainTitle,
        getTrendsTitle,
        getRecentlyOnDemandTitle,
        getLiveSeriesTitle,
    } = storeToRefs(HomePageStore);


    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-show-all-home'],
    });

    const { $navigator, } = useNuxtApp();
    const route = useRoute()
    const router = useRouter();
    const { t } = useI18n();

    const selectedProgram: Ref<ProgramGuideItem | null> = ref(null);
    const routeQueryType = route.query.type as string;
    const pageTitle: ComputedRef<string> = computed(() => {
        switch (routeQueryType) {
            case ShowAllContentType.Movies:
                return getMoviesTitle.value;
            case ShowAllContentType.Sports:
                return getSportsTitle.value;
            case ShowAllContentType.Kids:
                return getKidsTitle.value;
            case ShowAllContentType.News:
                return getNewsTitle.value;
            case ShowAllContentType.ContinueWatching:
                return getContinueWatchingTitle.value;
            case ShowAllContentType.WatchAgain:
                return getWatchAgainTitle.value;
            case ShowAllContentType.Trends:
                return getTrendsTitle.value;
            case ShowAllContentType.RecentlyOnDemand:
                return getRecentlyOnDemandTitle.value;
            case ShowAllContentType.Olympics:
                return getOlympicsTitle.value;
            case ShowAllContentType.LiveSeries:
                return getLiveSeriesTitle.value;
            default:
                Logger.captureError(`Unknown show all type: ${routeQueryType}` as unknown as Error);
                return "";
        }
    });

    const fetchMoreItems = async (): Promise<void> => {
        await loadShowAllNextPage();
    };
    const itemsSelected = (event: IEventSelected) => {
        switch (route.query.type) {
            case ShowAllContentType.ContinueWatching:
                onPlayOnDemandCardHomeShowAll(event.item as OnDemandCardData);
                break;

            case ShowAllContentType.RecentlyOnDemand:
                onPlayOnDemandCardHomeShowAll(event.item as OnDemandCardData);
                break;

            case ShowAllContentType.WatchAgain:
                onPlayOnDemandCardHomeShowAll(event.item as OnDemandCardData);
                break;
                
            case ShowAllContentType.Trends:
                onPlayTrends(event.item as ProgramCardData);
                break;

            default:
                onPlayProgram(event.item as ProgramCardData);
                break;
        }
    };
    const onPlayOnDemandCardHomeShowAll = (item: OnDemandCardData) => {
        if (!isGuest.value) {
            try {
                const playItem = item.getPlayItem();
                if (!playItem) {
                    throw new Error("playItem is null:" + JSON.stringify(item));
                }
                setViewedDetailItems(playItem);
                if (PlayItem.isSeries(playItem)) {
                    void fetchEpisodes(playItem);
                }

                if (!PlayItem.isSeries(playItem)) {
                    setCurrentItemDetail(item.getId());
                }

                startPlayingProgram(playItem);
                void router.push(linkItems.player.routerPath);
            } catch (error) {
                Logger.captureError(error as Error);
                setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
                setTooltipeShowing({visible: true});
            }
        } else {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
        }
    };
    const onPlayProgram = (item: ProgramCardData) => {
        if (isGuest.value) {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
            return;
        }

        const program = item.getProgram();

        if (!program) {
            return;
        }
        selectedProgram.value = program;

        if (PlayItem.isLive(program)) {
            startPlayingProgram(program);
            void router.push(linkItems.player.routerPath);
        } else {
            goToDetailsPageHomeShowAll();
        }
    };
    const onPlayTrends = (item: ProgramCardData) => {
        if (isGuest.value) {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
            return;
        }

        const program = item.getProgram();

        if (!program) {
            return;
        }
        selectedProgram.value = program;

        if (PlayItem.isLive(program)) {
            startPlayingProgram(program);
            void router.push(linkItems.player.routerPath);
        } else {
            goToTrendsDetailsPageHomeShowAll();
        }
    };
    const goToTrendsDetailsPageHomeShowAll = () => {
        try {
            if (!selectedProgram.value) {
                throw new Error("Program is not selected");
            }
            if (selectedProgram.value.recentAirTime) {
                goToDetailsPageHomeShowAll();
            } else {
                setViewedDetailItems(selectedProgram.value);
                const path = PlayItem.isSeries(selectedProgram.value) ?
                    `${linkItems.detailsVodSeries.routerPath}/${selectedProgram.value.id}` :
                    `${linkItems.detailsVodMovies.routerPath}/${selectedProgram.value.id}`;
                void router.push(path);
            }  
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
        
    };
    const goToDetailsPageHomeShowAll = () => {
        try {
            if (!selectedProgram.value) {
                throw new Error("Program is not selected");
            }
            setViewedDetailItems(selectedProgram.value);
            void router.push(`${linkItems.detailsPrograms.routerPath}/${selectedProgram.value.id}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };

    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };

    const keyBackListener = (): void => {
        router.go(-1);
    };
</script>
